import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { DeliveredDay } from '../types';

/** Odakle je vreme došlo — po tome se zna koliko mu se veruje. */
const SOURCE_LABEL: Record<string, string> = {
  'vozač': '',
  'vozač, na kraju ture': 'vozač potvrdio na kraju ture',
  'operater': 'potvrdio operater',
  'stanica': 'po odlasku sa stanice',
  'gps': 'po GPS-u, vozač nije štiklirao',
  'nema': 'bez vremena'
};

function time(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

function day(value: string): string {
  return new Date(value).toLocaleDateString('sr-RS', {
    weekday: 'long', day: '2-digit', month: '2-digit'
  });
}

/**
 * Šta je kog dana isporučeno.
 *
 * Pitanje „je li ovo otišlo i kad" do sad je tražilo otvaranje pojedinačne
 * ture. Ovde stoji po danima, najnoviji dan prvi.
 *
 * Uz vreme stoji i ODAKLE je došlo. Vozačevo štikliranje se ne razlikuje od
 * GPS merenja bojom nego rečima, jer razlika nije kozmetička: prvo znači da je
 * čovek predao robu, drugo samo da je vozilo bilo tu.
 */
export default function Delivered() {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState<DeliveredDay[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await api.delivered(days));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="health health--bad">{error}</div>;
  if (!report) return <p className="muted">Učitavam…</p>;

  return (
    <section className="panel">
      <div className="tourdetail__head">
        <h2>Isporučeno</h2>
        <span className="tourdetail__actions">
          {[7, 14, 30].map((n) => (
            <button
              key={n}
              type="button"
              className={n === days ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
              onClick={() => setDays(n)}
            >
              {n} dana
            </button>
          ))}
        </span>
      </div>

      {report.length === 0 ? (
        <p className="empty">
          <strong>Nema nijedne isporuke u tom periodu.</strong><br />
          Ovde se vidi tek ono što je vozač štiklirao ili što je tura zatvorila.
        </p>
      ) : (
        report.map((d) => (
          <div key={d.date} className="delivered__day">
            <p className="delivered__head">
              {day(d.date)}
              <span className="muted">
                {' '}· {d.stops} {d.stops === 1 ? 'isporuka' : 'isporuka'} · {d.pieces} kom
              </span>
            </p>

            <ul className="rows">
              {d.deliveries.map((row) => (
                <li key={row.stopId}>
                  <span className="row__name">
                    {row.label}
                    {row.city ? `, ${row.city}` : ''}
                    {row.hasSignature && <span className="flag flag--ok"> potpisano</span>}
                  </span>

                  <span className="row__side">
                    <span className={row.source === 'vozač' ? 'flag' : 'flag flag--warn'}>
                      {time(row.at)}
                    </span>
                  </span>

                  <span className="row__meta">
                    {row.items.join(', ')}
                    {row.documentKey ? ` · ${row.documentKey}` : ''}
                    {row.driver ? ` · ${row.driver}` : ''}
                    {SOURCE_LABEL[row.source] ? ` · ${SOURCE_LABEL[row.source]}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}
